/** @jsxRuntime classic */
/** @jsx jsx */
import { useMemo, useState, useCallback } from 'react';
import { jsx, css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { getTemplates } from './flowTemplates';
import { useFlowsActions } from '../../../actions/FlowActions';
import TemplateSettings from './TemplateSettings';

export default function FlowTemplateDialog({ open, onClose, data }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { createFlow } = useFlowsActions();
  const [settings, setSettings] = useState({});

  const template = useMemo(() => {
    return getTemplates(t, i18n).find((x) => x.id === data?.id);
  }, [data?.id, t, i18n]);

  // Check if all required settings are set
  const areSettingsValid = useMemo(() => {
    if (!template?.data?.nodes) return true;
    
    return template.data.nodes.every(node => {
      if (node.type === 'slack') {
        return !!settings[node.id]?.destination;
      }
      if (node.type === 'googleDrive') {
        return !!settings[node.id]?.folder;
      }
      return true;
    });
  }, [template, settings]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  async function onCreateFlow(showAutomationDialog = false) {
    // Apply settings to the template data

    const templateData = {
      ...template.data,
      nodes: template.data.nodes.map(node => {
        if (settings[node.id]) {
          if (node.type === 'googleDrive') {
            return {
              ...node,
              data: {
                ...node.data,
                folder: { id: settings[node.id].folder, name: settings[node.id].folderName },
              }
            };
          }
          return {
            ...node,
            data: {
              ...node.data,
              ...settings[node.id]
            }
          };
        }
        return node;
      })
    };

    const flow = await createFlow({
      name: template.title,
      data: templateData,
    });

    if (flow?.id) {
      navigate(`/flow/${flow.id}${showAutomationDialog ? '?showAutomation=true' : ''}`);
    }
    onClose();
  }

  const renderIcon = (Icon, idx) => {
    if (typeof Icon === 'string') {
      return (
        <div
          key={idx}
          css={css`
            border-radius: 8px;
            background: #eef4fd;
            padding: 8px;
            width: 54px;
            height: 54px;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <img
            src={Icon}
            alt=""
            css={css`
              width: 24px;
              height: 24px;
            `}
          />
        </div>
      );
    }

    return (
      <div
        key={idx}
        css={css`
          border-radius: 8px;
          background: #eef4fd;
          padding: 8px;
          width: 54px;
          height: 54px;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
        `}
      >
        <Icon color="primary" />
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      css={css`
        z-index: 4000;
      `}
    >
      <DialogTitle
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        <Typography
          variant="subtitle1"
          css={css`
            font-weight: 600;
          `}
        >
          {template?.title}
        </Typography>

        <IconButton
          css={css`
            border: 1px solid rgba(0, 0, 0, 0.23);
            border-radius: 0.5rem;
            margin-left: auto;
          `}
          onClick={handleClose}
          size="small"
        >
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div
          css={css`
            display: flex;
            padding: 24px 24px 24px 16px;
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
            border-radius: 8px;
            border: 1px solid rgba(0, 0, 0, 0.23);
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              gap: 16px;
            `}
          >
            <div
              css={css`
                display: flex;
                gap: 8px;
              `}
            >
              {template?.icons.map((Icon, idx) => renderIcon(Icon, idx))}
            </div>
            <Typography
              variant="subtitle1"
              css={css`
                color: #15185199;
                font-size: 0.9375rem;
              `}
            >
              {template?.description}
            </Typography>
          </div>
        </div>

        <TemplateSettings 
          template={template}
          settings={settings}
          onChange={setSettings}
        />
      </DialogContent>
      <DialogActions
        css={css`
          padding: 16px 24px;
          margin: 0;
          gap: 8px;
        `}
      >
        <Button 
          variant="outlined"
          onClick={() => onCreateFlow(false)}
          disabled={!areSettingsValid}
          startIcon={<EditIcon />}
        >
          {t('Redigera')}
        </Button>
        <Button 
          variant="contained"
          onClick={() => onCreateFlow(true)}
          disabled={!areSettingsValid}
          startIcon={<AutoFixHighIcon />}
        >
          {t('Skapa automatisering')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
